'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { ProductPriceFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductPriceFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { useIntl } from 'react-intl';
import { useFormatDiscount } from '@packages/shared/src/hooks/useFormatDiscount/useFormatDiscount';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { usePriceFormatter } from '@packages/shared/src/hooks/usePriceFormatter/usePriceFormatter';
import { PriceDiscountLocalized } from '../Price/PriceDiscountLocalized/PriceDiscountLocalized';
import { PriceUnitPriceLocalized } from '../Price/PriceUnitPriceLocalized/PriceUnitPriceLocalized';
import { priceUnitPriceMessages } from '../Price/PriceUnitPriceLocalized/messages';
import { PriceValueLocalized } from '../Price/PriceValueLocalized/PriceValueLocalized';

/* GraphQL */ `
  fragment ProductPriceFragment on ProductPrice {
    amount
    currency
    saving {
      type
      value
    }
    unit
    uvp
    value
    valueOld
    valueUnit
  }
`;

export type PriceDVProps = {
  data: FragmentType<typeof ProductPriceFragmentFragmentDoc>;
};

export const PriceDV = ({ data }: PriceDVProps) => {
  const formatPrice = usePriceFormatter('PRODUCT');
  const { formatDiscount } = useFormatDiscount();
  const { formatMessage } = useIntl();

  const { amount, currency, saving, unit, uvp, value, valueOld, valueUnit } =
    unmask<typeof ProductPriceFragmentFragmentDoc>(data);

  return (
    <Price>
      {valueOld && (
        <PriceDiscountLocalized
          oldPrice={`${
            uvp
              ? formatMessage({
                  id: 'priceDV.discount.uvp',
                  defaultMessage: 'UVP',
                })
              : formatMessage({
                  id: 'priceDV.discount.insteadOf',
                  defaultMessage: 'statt',
                })
          } ${formatPrice(valueOld, currency)}`}
          oldPriceType={uvp ? 'msrp' : undefined}
          discount={`- ${formatDiscount(
            saving?.type === 'CURRENCY'
              ? {
                  currency,
                  priceDifference: saving.value,
                }
              : {
                  discountPercentage: saving?.value || 0,
                },
          )}`}
        />
      )}
      <PriceValueLocalized className="text-size-body-lg lg:text-size-body-2xl">
        {formatPrice(value, currency)}
      </PriceValueLocalized>
      {valueUnit && (
        <PriceUnitPriceLocalized>
          {formatPrice(valueUnit, currency)}
          <span className="sr-only">{formatMessage(priceUnitPriceMessages.perUnit)}</span>
          <span aria-hidden>{'\u00A0/\u00A0'}</span>
          {`${amount ? `${amount} ` : ''}${unit}`}
        </PriceUnitPriceLocalized>
      )}
    </Price>
  );
};
